import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Users, TrendingUp, TrendingDown, Minus, ChevronRight, Activity,
  ArrowUpRight, ArrowDownRight, Target, AlertTriangle, Search,
} from 'lucide-react';
import { VUKZ, VUKZ_GRADIENT } from '../theme/vukzTheme';
import PageHeader from '../components/layout/PageHeader';
import { getCategoriesSummary } from '../services/managerPerformanceService';
import { useSportsLang } from '../contexts/SportsLangContext';
import { useActiveContext } from '../contexts/ActiveContextContext';
import { useRBAC } from '../contexts/RBACContext';

const LABELS = {
  pt: {
    title: 'Categorias',
    subtitle: 'Controle de performance por categoria de idade',
    badge: 'GESTÃO',
    searchPlaceholder: 'Buscar categoria...',
    colCategory: 'Categoria',
    colAthletes: 'Atletas',
    colReadiness: 'Readiness (7d)',
    colLoad: 'Carga Semanal',
    colAdherence: 'Aderência',
    colRisks: 'Riscos',
    colGoals: 'Staff responsável',
    mobileLoad: 'Carga',
    mobileAdherence: 'Aderência',
    mobileRisks: 'Riscos',
    athletes: 'atletas',
    categoriesWord: 'categorias',
  },
  en: {
    title: 'Categories',
    subtitle: 'Performance control by age category',
    badge: 'MANAGEMENT',
    searchPlaceholder: 'Search category...',
    colCategory: 'Category',
    colAthletes: 'Athletes',
    colReadiness: 'Readiness (7d)',
    colLoad: 'Weekly Load',
    colAdherence: 'Adherence',
    colRisks: 'Risks',
    colGoals: 'Responsible staff',
    mobileLoad: 'Load',
    mobileAdherence: 'Adherence',
    mobileRisks: 'Risks',
    athletes: 'athletes',
    categoriesWord: 'categories',
  },
};

// ─── Reusable ─────────────────────────────────────────────────────────────────

const TrendIcon = ({ trend }) => {
  if (trend === 'up') return <ArrowUpRight size={14} style={{ color: VUKZ.success }} />;
  if (trend === 'down') return <ArrowDownRight size={14} style={{ color: VUKZ.error }} />;
  return <Minus size={14} style={{ color: VUKZ.textLight }} />;
};

const ReadinessBadge = ({ value }) => {
  const v = value ?? 0;
  const color = v >= 7.5 ? VUKZ.success : v >= 6.5 ? VUKZ.warning : VUKZ.error;
  return (
    <span className="text-xs font-black px-2 py-0.5 rounded-full" style={{ backgroundColor: `${color}15`, color }}>
      {v.toFixed(1)}
    </span>
  );
};

const SkeletonRows = ({ count = 5 }) => (
  <div className="space-y-2">
    {Array.from({ length: count }, (_, i) => (
      <div key={i} className="h-20 rounded-xl animate-pulse" style={{ backgroundColor: VUKZ.primaryLight }} />
    ))}
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════════
// Categories Page — Tabela com drill-down
// ═══════════════════════════════════════════════════════════════════════════════

const CategoriesPage = ({ embedded = false }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;
  const { clubId: activeClubId } = useActiveContext();
  const { clubId: rbacClubId } = useRBAC();
  // TODO(SEC-CONFLATION): rbacClubId vem de user_roles.club_id (JWT). O service
  // getCategoriesSummary espera institution_id — coincidem no MVP (3 tenants), quebra
  // no 4º cliente ou primeiro com club_id ≠ institution_id.
  // Ver SECURITY_BACKLOG.md → SEC-CONFLATION e SEC-ACTIVECONTEXT-BOOTSTRAP.
  const effectiveClubId = activeClubId || rbacClubId;

  useEffect(() => {
    if (!effectiveClubId) {
      console.warn('[CategoriesPage] effectiveClubId=null (sem clube no ActiveContext nem no JWT) — nada a carregar.');
      setLoading(false);
      return;
    }
    const load = async () => {
      setLoading(true);
      try {
        const cats = await getCategoriesSummary(effectiveClubId);
        setCategories(cats);
      } catch (err) {
        console.error('Error loading categories:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [effectiveClubId]);

  const filtered = categories.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className={embedded ? '' : 'min-h-screen'}
      style={embedded ? undefined : { backgroundColor: VUKZ.bg }}>
      <div className={embedded ? '' : 'max-w-7xl mx-auto px-4 lg:px-6 py-6'}>

        {/* Header — omitido quando embutida na Equipe (E1) */}
        {!embedded && (
          <PageHeader
            title={t.title}
            subtitle={t.subtitle}
            badgeText={t.badge}
            className="mb-6"
          />
        )}

        {/* Search */}
        <div className="bg-white rounded-xl border mb-6 flex items-center gap-2 px-4" style={{ borderColor: VUKZ.border }}>
          <Search size={14} style={{ color: VUKZ.textLight }} />
          <input
            type="text"
            placeholder={t.searchPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full py-3 text-sm bg-transparent outline-none"
            style={{ color: VUKZ.text }}
          />
        </div>

        {loading ? (
          <SkeletonRows count={5} />
        ) : (
          <>
            {/* Table Header (desktop) */}
            <div className="hidden md:grid grid-cols-12 gap-3 px-4 py-2 text-[10px] font-bold uppercase" style={{ color: VUKZ.textLight }}>
              <div className="col-span-2">{t.colCategory}</div>
              <div className="col-span-1 text-center">{t.colAthletes}</div>
              <div className="col-span-2 text-center">{t.colReadiness}</div>
              <div className="col-span-2 text-center">{t.colLoad}</div>
              <div className="col-span-2 text-center">{t.colAdherence}</div>
              <div className="col-span-1 text-center">{t.colRisks}</div>
              <div className="col-span-2">{t.colGoals}</div>
            </div>

            {/* Rows */}
            <div className="space-y-2">
              {filtered.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/manager/categories/${cat.id}`}
                  className="block bg-white rounded-xl border p-4 hover:shadow-md transition-all group"
                  style={{ borderColor: VUKZ.border }}
                >
                  {/* Desktop */}
                  <div className="hidden md:grid grid-cols-12 gap-3 items-center">
                    <div className="col-span-2">
                      <h3 className="text-sm font-extrabold group-hover:opacity-80" style={{ color: VUKZ.text }}>{cat.name}</h3>
                      <p className="text-[10px]" style={{ color: VUKZ.textLight }}>{cat.age_range}</p>
                    </div>
                    <div className="col-span-1 text-center">
                      <span className="text-sm font-bold" style={{ color: VUKZ.text }}>{cat.athlete_count}</span>
                      <p className="text-[9px]" style={{ color: VUKZ.textLight }}>
                        {cat.gender_split.m}M · {cat.gender_split.f}F · {cat.active_count} ativos
                      </p>
                    </div>
                    <div className="col-span-2 flex items-center justify-center gap-2">
                      <TrendIcon trend={cat.readiness_trend} />
                      <ReadinessBadge value={cat.readiness_avg} />
                      <span className="text-[10px] font-semibold" style={{ color: cat.readiness_trend === 'up' ? VUKZ.success : cat.readiness_trend === 'down' ? VUKZ.error : VUKZ.textLight }}>
                        {cat.readiness_delta}
                      </span>
                    </div>
                    <div className="col-span-2 text-center">
                      <span className="text-sm font-bold" style={{ color: VUKZ.text }}>{cat.weekly_load.volume_km} km</span>
                      <p className="text-[9px]" style={{ color: VUKZ.textLight }}>
                        Int {cat.weekly_load.intensity_avg} · Mon {cat.weekly_load.monotony}
                      </p>
                    </div>
                    <div className="col-span-2 text-center">
                      <div className="inline-flex items-center gap-1.5">
                        <div className="w-16 h-2 rounded-full" style={{ backgroundColor: VUKZ.primaryLight }}>
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: `${cat.adherence_pct}%`,
                              backgroundColor: cat.adherence_pct >= 85 ? VUKZ.success : cat.adherence_pct >= 70 ? VUKZ.warning : VUKZ.error,
                            }}
                          />
                        </div>
                        <span className="text-xs font-bold" style={{ color: VUKZ.text }}>{cat.adherence_pct}%</span>
                      </div>
                    </div>
                    <div className="col-span-1 text-center">
                      {cat.risk_count > 0 ? (
                        <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: '#fee2e2', color: VUKZ.error }}>
                          <AlertTriangle size={10} /> {cat.risk_count}
                        </span>
                      ) : (
                        <span className="text-xs font-bold" style={{ color: VUKZ.success }}>0</span>
                      )}
                    </div>
                    <div className="col-span-2 flex items-center justify-between">
                      <span className="text-[10px] truncate pr-2" style={{ color: VUKZ.textLight }}>{cat.responsible_staff || '—'}</span>
                      <ChevronRight size={14} style={{ color: VUKZ.textLight }} />
                    </div>
                  </div>

                  {/* Mobile */}
                  <div className="md:hidden">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h3 className="text-sm font-extrabold" style={{ color: VUKZ.text }}>{cat.name}</h3>
                        <p className="text-[10px]" style={{ color: VUKZ.textLight }}>{cat.age_range} · {cat.athlete_count} {t.athletes}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <TrendIcon trend={cat.readiness_trend} />
                        <ReadinessBadge value={cat.readiness_avg} />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="text-center p-2 rounded-lg" style={{ backgroundColor: VUKZ.bg }}>
                        <div className="text-xs font-bold" style={{ color: VUKZ.text }}>{cat.weekly_load.volume_km} km</div>
                        <div className="text-[9px]" style={{ color: VUKZ.textLight }}>{t.mobileLoad}</div>
                      </div>
                      <div className="text-center p-2 rounded-lg" style={{ backgroundColor: VUKZ.bg }}>
                        <div className="text-xs font-bold" style={{ color: VUKZ.text }}>{cat.adherence_pct}%</div>
                        <div className="text-[9px]" style={{ color: VUKZ.textLight }}>{t.mobileAdherence}</div>
                      </div>
                      <div className="text-center p-2 rounded-lg" style={{ backgroundColor: cat.risk_count > 0 ? '#fef2f2' : VUKZ.bg }}>
                        <div className="text-xs font-bold" style={{ color: cat.risk_count > 0 ? VUKZ.error : VUKZ.text }}>{cat.risk_count}</div>
                        <div className="text-[9px]" style={{ color: VUKZ.textLight }}>{t.mobileRisks}</div>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {/* Total row */}
            <div className="mt-4 bg-white rounded-xl border p-4 flex items-center justify-between" style={{ borderColor: VUKZ.border }}>
              <span className="text-xs font-bold" style={{ color: VUKZ.textLight }}>Total: {categories.length} {t.categoriesWord}</span>
              <span className="text-xs font-bold" style={{ color: VUKZ.text }}>
                {(categories || []).reduce((s, c) => s + (c.athlete_count || 0), 0)} {t.athletes}
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CategoriesPage;
